import { useEffect, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/SimpleToast";
import { useVoiceDictation } from "@/hooks/useVoiceDictation";
import { VoiceRefineModal } from "@/components/voice/VoiceRefineModal";

const fmtTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

/**
 * Universeller Mikrofon-/Diktat-Button. Nimmt Audio auf, visualisiert die
 * Lautstärke (pulsierender Ring), stoppt automatisch nach 3 Minuten (Blinken
 * 10 s vorher), transkribiert per Whisper, lässt den Text per KI aufbereiten und
 * öffnet ein Smart-Modal. Nach Bestätigung wird der Text via `onTranscript`
 * in das Ausgangsfeld eingefügt. Einheitlich für alle Felder im CRM.
 */
export function VoiceInputButton({
  onTranscript,
  size = "icon",
  variant = "ghost",
  className = "",
  title = "Spracheingabe (Diktat)",
  label,
}) {
  const toast = useToast();
  const [modalOpen, setModalOpen] = useState(false);

  const {
    supported, recording, processing, volume, secondsLeft, warning, result,
    start, stop, cancel, clearResult,
  } = useVoiceDictation({
    onError: (e) => {
      if (e?.type === "permission") toast.error("Mikrofon-Zugriff verweigert. Bitte im Browser erlauben.");
      else if (e?.type === "empty") toast.info("Es wurde nichts aufgenommen.");
      else toast.error("Sprachverarbeitung fehlgeschlagen. Bitte erneut versuchen.");
    },
  });

  // Sobald das KI-Ergebnis vorliegt, Smart-Modal öffnen
  useEffect(() => {
    if (result) setModalOpen(true);
  }, [result]);

  if (!supported) return null;

  // Ring-Skalierung anhand der Lautstärke (1.0 – 1.8)
  const ringScale = 1 + Math.min(volume, 1) * 0.8;

  const handleConfirm = (text) => {
    onTranscript?.(text);
    clearResult();
    toast.success("Diktat übernommen");
  };

  const handleModalOpenChange = (o) => {
    setModalOpen(o);
    if (!o) clearResult();
  };

  if (recording) {
    return (
      <span className="inline-flex items-center gap-2" data-testid="voice-recording-wrap">
        <span className="relative inline-flex items-center justify-center">
          <span
            aria-hidden
            className={`absolute rounded-full bg-red-500/30 ${warning ? "animate-ping" : ""}`}
            style={{ width: 40, height: 40, transform: `scale(${ringScale})`, transition: "transform 80ms linear" }}
          />
          <Button
            type="button"
            variant="destructive"
            size={label ? "sm" : size}
            onClick={stop}
            className={`relative z-10 rounded-full ${warning ? "animate-pulse" : ""}`}
            title="Aufnahme beenden"
            data-testid="voice-stop-btn"
          >
            <Square className="w-4 h-4" />
            {label && <span className="ml-1 text-xs">Stopp</span>}
          </Button>
        </span>
        <span
          className={`text-xs tabular-nums font-medium ${warning ? "text-red-600 animate-pulse" : "text-muted-foreground"}`}
          data-testid="voice-timer"
        >
          {fmtTime(secondsLeft)}
        </span>
      </span>
    );
  }

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={label ? "sm" : size}
        onClick={start}
        disabled={processing}
        className={className}
        title={title}
        data-testid="voice-input-btn"
      >
        {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
        {label && <span className="ml-1 text-xs">{processing ? "Verarbeite…" : label}</span>}
      </Button>

      <VoiceRefineModal
        open={modalOpen}
        onOpenChange={handleModalOpenChange}
        result={result}
        onConfirm={handleConfirm}
      />
    </>
  );
}

export default VoiceInputButton;
